// Ambient fauna layout (dominion-path neighbours: docs/02-systems/ambient-fauna.md,
// slices W1+W2). Pure: candidate sites + assignments from tiles/biomes only,
// so tests cover gating, caps and determinism. Animation runs in FaunaLayer.

import type { SceneNode } from '@aod/protocol';
import { hexNoise, waterSurfaceY } from '../elevation.js';
import { HEX_DIRS, edgeMidpointOffset } from '../territory.js';

const WATER_BIOMES = new Set(['ocean', 'coast']);

/** A spot where water life can appear (x/z world, y surface). */
export interface WaterSite {
  readonly key: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** River-channel site (sits on a land tile's river edge, not open water). */
  readonly isRiver: boolean;
}

/**
 * Water sites for fish jumps and ducks: open water tiles (ocean/coast) plus
 * river-channel midpoints on land tiles carrying a river edge. Visibility is
 * the caller's concern (pass pre-filtered tiles or filter sites after).
 */
export function waterSites(
  tiles: ReadonlyArray<SceneNode>,
  biomeMap: Map<string, string>,
): WaterSite[] {
  const out: WaterSite[] = [];
  for (const tile of tiles) {
    const props = (tile.props ?? {}) as Record<string, unknown>;
    const q = props.q as number | undefined;
    const r = props.r as number | undefined;
    if (q === undefined || r === undefined) continue;
    const biome = biomeMap.get(`${q},${r}`);
    const [x, , z] = tile.transform.position;
    if (biome !== undefined && WATER_BIOMES.has(biome)) {
      out.push({
        key: `${q},${r}`,
        x,
        y: waterSurfaceY(q, r, biomeMap),
        z,
        isRiver: false,
      });
      continue;
    }
    // Land tile with a river edge: the drawn channel crosses the edge
    // midpoint — jump site sits there, at land surface height.
    const riverEdges = Number(props.riverEdges ?? 0);
    if (riverEdges > 0) {
      const dir = Math.log2(riverEdges & -riverEdges); // lowest set bit
      const [dq, dr] = HEX_DIRS[dir] ?? HEX_DIRS[0]!;
      const [ox, oz] = edgeMidpointOffset(dq, dr);
      out.push({
        key: `${q},${r}:river${dir}`,
        x: x + ox,
        y: tile.transform.position[1],
        z: z + oz,
        isRiver: true,
      });
    }
  }
  return out;
}

/** Fish jump assignment: up to `cap` sites, deterministically chosen and
 * phase-staggered so jumps don't synchronize. */
export interface FishJump {
  readonly site: WaterSite;
  /** Phase in [0,1) — offset into the jump cycle. */
  readonly phase: number;
  /** Cycle length in seconds (4–9 s). */
  readonly period: number;
}

export const FISH_CAP = 3;

export function fishJumps(sites: readonly WaterSite[]): FishJump[] {
  const scored = sites
    .map((site) => ({ site, h: hexNoise(site.x * 2.17, site.z * 1.93) }))
    .sort((a, b) => a.h - b.h)
    .slice(0, FISH_CAP);
  return scored.map(({ site, h }, i) => ({
    site,
    phase: (h * 7.31 + i * 0.37) % 1,
    period: 4 + h * 5,
  }));
}

/** Duck assignment: up to `cap` ducks in pairs on the largest open-water
 * sites (rivers excluded — the channel is too narrow to read a wake). */
export interface Duck {
  readonly site: WaterSite;
  /** Angle around the site the duck circles from. */
  readonly startAngle: number;
  /** Angular speed (rad/s) — slow paddle. */
  readonly speed: number;
  /** Orbit radius (tiles are ~1.7 wide; stay well inside). */
  readonly radius: number;
}

export const DUCK_CAP = 8;

export function ducks(sites: readonly WaterSite[]): Duck[] {
  const open = sites
    .filter((s) => !s.isRiver)
    .map((site) => ({ site, h: hexNoise(site.x * 1.37 + 5, site.z * 1.71 - 3) }))
    .sort((a, b) => b.h - a.h)
    .slice(0, Math.ceil(DUCK_CAP / 2));
  const out: Duck[] = [];
  for (const { site, h } of open) {
    for (let i = 0; i < 2 && out.length < DUCK_CAP; i++) {
      const dh = hexNoise(site.x * 3.7 + i * 11, site.z * 4.1 - i * 7);
      out.push({
        site,
        startAngle: dh * Math.PI * 2,
        // ~0.15–0.3 rad/s — a leisurely paddle; the pair opposes direction.
        speed: (0.15 + dh * 0.15) * (i === 0 ? 1 : -1),
        radius: 0.16 + h * 0.14,
      });
    }
  }
  return out;
}

// ── W3/W4: land fauna — rabbits, the fox, sheep and pigs ────────────────
// Same discipline as the water layer: pure layout from tiles + biomes,
// hard caps, deterministic seeds; animation in FaunaLayer.

/** A land anchor where a small group of animals lives. */
export interface LandAnchor {
  readonly key: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

interface LandTile {
  tile: SceneNode;
  q: number;
  r: number;
  x: number;
  y: number;
  z: number;
  biome: string;
}

function landTiles(tiles: ReadonlyArray<SceneNode>, biomeMap: Map<string, string>): LandTile[] {
  const out: LandTile[] = [];
  for (const tile of tiles) {
    const props = (tile.props ?? {}) as Record<string, unknown>;
    const q = props.q as number | undefined;
    const r = props.r as number | undefined;
    if (q === undefined || r === undefined) continue;
    const biome = biomeMap.get(`${q},${r}`);
    if (biome === undefined || WATER_BIOMES.has(biome)) continue;
    const [x, , z] = tile.transform.position;
    out.push({ tile, q, r, x, y: tile.transform.position[1], z, biome });
  }
  return out;
}

/** Pick well-spread anchors: highest noise first, rejecting anchors too
 * close to an already-chosen one (min world distance). */
function spreadAnchors(pool: LandTile[], count: number, minDist: number, seed: number): LandTile[] {
  const scored = pool
    .map((t) => ({ t, h: hexNoise(t.x * seed, t.z * seed * 1.3) }))
    .sort((a, b) => b.h - a.h);
  const picked: LandTile[] = [];
  for (const { t } of scored) {
    if (picked.length >= count) break;
    if (picked.every((p) => Math.hypot(p.x - t.x, p.z - t.z) >= minDist)) picked.push(t);
  }
  return picked;
}

function anchorOf(t: LandTile): LandAnchor {
  return { key: `${t.q},${t.r}`, x: t.x, y: t.y, z: t.z };
}

// ── Rabbits (plains) ─────────────────────────────────────────────────────

export const RABBIT_CAP = 12;
export const WARREN_COUNT = 4;

export interface Rabbit {
  readonly anchor: LandAnchor;
  /** Offset from the anchor (world units). */
  readonly ox: number;
  readonly oz: number;
  /** Phase in [0,1) into the nibble-hop cycle. */
  readonly phase: number;
}

export function rabbitWarrens(
  tiles: ReadonlyArray<SceneNode>,
  biomeMap: Map<string, string>,
): Rabbit[] {
  const plains = landTiles(tiles, biomeMap).filter((t) => t.biome === 'plains');
  const anchors = spreadAnchors(plains, WARREN_COUNT, 3, 7.7);
  const out: Rabbit[] = [];
  for (const anchor of anchors) {
    const count = 2 + Math.floor(hexNoise(anchor.x * 5.1, anchor.z * 4.3) * 3); // 2–4
    for (let i = 0; i < count && out.length < RABBIT_CAP; i++) {
      const h = hexNoise(anchor.x * 6.3 + i * 13, anchor.z * 5.9 - i * 9);
      out.push({
        anchor: anchorOf(anchor),
        ox: (hexNoise(h * 91, i * 17) - 0.5) * 0.7,
        oz: (hexNoise(i * 23, h * 87) - 0.5) * 0.7,
        phase: h,
      });
    }
  }
  return out;
}

// ── The fox (forest edge) ────────────────────────────────────────────────

export const FOX_CAP = 3;

export interface FoxRoute {
  /** Loop of 2–3 world points; the fox trots the loop with pauses. */
  readonly points: Array<{ x: number; y: number; z: number }>;
  readonly phase: number;
}

/** Forest tiles with at least one non-forest land neighbour — the prowl line. */
export function foxRoutes(
  tiles: ReadonlyArray<SceneNode>,
  biomeMap: Map<string, string>,
): FoxRoute[] {
  const all = landTiles(tiles, biomeMap);
  const byKey = new Map(all.map((t) => [`${t.q},${t.r}`, t]));
  const edges = all.filter((t) => {
    if (t.biome !== 'forest') return false;
    for (let d = 0; d < HEX_DIRS.length; d++) {
      const n = byKey.get(`${t.q + HEX_DIRS[d]![0]},${t.r + HEX_DIRS[d]![1]}`);
      if (n && n.biome !== 'forest') return true;
    }
    return false;
  });
  const anchors = spreadAnchors(edges, FOX_CAP, 4, 3.1);
  return anchors.map((anchor) => {
    // Route: the anchor plus up to two nearby edge tiles (loop through them).
    const nearby = edges
      .filter((t) => t !== anchor && Math.hypot(t.x - anchor.x, t.z - anchor.z) < 3.4)
      .slice(0, 2);
    const points = [anchor, ...nearby].map((t) => ({ x: t.x, y: t.y, z: t.z }));
    return { points, phase: hexNoise(anchor.x * 9.7, anchor.z * 8.1) };
  });
}

// ── Sheep (plains) and pigs (farms) ──────────────────────────────────────

export const SHEEP_CAP = 12;
export const HERD_COUNT = 3;
export const PIG_CAP = 6;

export interface Grazer {
  readonly anchor: LandAnchor;
  readonly ox: number;
  readonly oz: number;
  readonly phase: number;
}

export function sheepHerds(
  tiles: ReadonlyArray<SceneNode>,
  biomeMap: Map<string, string>,
): Grazer[] {
  const plains = landTiles(tiles, biomeMap).filter((t) => t.biome === 'plains');
  const anchors = spreadAnchors(plains, HERD_COUNT, 4, 5.3);
  const out: Grazer[] = [];
  for (const anchor of anchors) {
    const count = 3 + Math.floor(hexNoise(anchor.x * 4.7, anchor.z * 3.9) * 3); // 3–5
    for (let i = 0; i < count && out.length < SHEEP_CAP; i++) {
      const h = hexNoise(anchor.x * 7.1 + i * 19, anchor.z * 6.7 - i * 11);
      out.push({
        anchor: anchorOf(anchor),
        ox: (hexNoise(h * 71, i * 29) - 0.5) * 0.8,
        oz: (hexNoise(i * 31, h * 69) - 0.5) * 0.8,
        phase: h,
      });
    }
  }
  return out;
}

export function pigSties(tiles: ReadonlyArray<SceneNode>, biomeMap: Map<string, string>): Grazer[] {
  const farms = landTiles(tiles, biomeMap).filter(
    (t) => ((t.tile.props ?? {}) as Record<string, unknown>).improvement === 'farm',
  );
  const anchors = spreadAnchors(farms, 3, 2, 4.9);
  const out: Grazer[] = [];
  for (const anchor of anchors) {
    const count = 2 + Math.floor(hexNoise(anchor.x * 3.7, anchor.z * 2.9) * 2); // 2–3
    for (let i = 0; i < count && out.length < PIG_CAP; i++) {
      const h = hexNoise(anchor.x * 8.3 + i * 37, anchor.z * 7.9 - i * 21);
      out.push({
        anchor: anchorOf(anchor),
        // Pigs stay in the field: tighter offset than the other cast.
        ox: (hexNoise(h * 61, i * 41) - 0.5) * 0.4,
        oz: (hexNoise(i * 43, h * 59) - 0.5) * 0.4,
        phase: h,
      });
    }
  }
  return out;
}
